/**
 * DataCard.jsx
 * Renders structured API data returned in the `data` field.
 * Handles: arrays (table), single objects (key-value), and nested content arrays.
 *
 * Shows ALL relevant details matching the ATS platform tables:
 *  - Candidate List: Name, Job Profile, Degree, Skills, Location, Source, Date Added, Phone, Email, Experience, Status
 *  - Applied Candidates: Name, Phone, Job Profile, Stage, Applied Date, Matching Score, Source
 *  - Job List: Position, Job Code, Location, Closing Date, Applied, Screen, Shortlist, Interview, Hired, Offer, Status
 */

import React, { useState, useEffect } from "react";
import "../ChatPage.css";

// ── Smart value formatting ──────────────────────────────────────────────────

function formatEpoch(val) {
  if (!val) return null;
  const num = typeof val === "string" ? Number(val) : val;
  if (isNaN(num)) return null;
  // Epoch milliseconds are > 1e12; epoch seconds are < 1e12
  const ms = num > 1e12 ? num : num * 1000;
  if (ms < 0 || ms > 4e15) return null; // sanity check
  const d = new Date(ms);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateString(val) {
  if (!val || typeof val !== "string") return null;
  // ISO date strings like "2026-05-18T10:00:00"
  if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
  }
  return null;
}

function renderValue(val) {
  if (val === null || val === undefined) return <span className="hn-null">—</span>;
  if (typeof val === "boolean") return val ? "Yes" : "No";

  // Arrays — join with commas (e.g. skills list)
  if (Array.isArray(val)) {
    if (val.length === 0) return <span className="hn-null">—</span>;
    // Array of objects (e.g. candidateSkills) → extract name/skill field
    if (typeof val[0] === "object" && val[0] !== null) {
      const names = val.map(
        (item) => item.skill || item.name || item.value || JSON.stringify(item)
      );
      return names.join(", ");
    }
    return val.join(", ");
  }

  // Objects — show as compact JSON
  if (typeof val === "object") {
    return <pre className="hn-pre">{JSON.stringify(val, null, 2)}</pre>;
  }

  const str = String(val);

  // Try to format as date (epoch millis)
  if (typeof val === "number" && val > 1e11) {
    const formatted = formatEpoch(val);
    if (formatted) return formatted;
  }

  // Try ISO date string
  if (typeof val === "string") {
    const formatted = formatDateString(val);
    if (formatted) return formatted;
  }

  return str;
}

// ── Column definitions for known API types ──────────────────────────────────

const CANDIDATE_COLUMNS = [
  { label: "Name", keys: ["name", "fullName", "full_name", "firstName", "first_name", "candidateName", "candidate_name"] },
  { label: "Job Profile", keys: ["jobProfile", "job_profile", "designation", "role", "title", "currentDesignation"] },
  { label: "Degree", keys: ["degree", "degrees", "education", "qualification", "highestDegree", "highest_degree"] },
  { label: "Skills", keys: ["skills", "candidateSkills", "skill", "keySkills", "key_skills"] },
  { label: "Location", keys: ["location", "presentCity", "present_city", "city", "currentLocation", "current_location"] },
  { label: "Source", keys: ["source", "candidateSource", "candidate_source", "sourceType", "source_type"] },
  { label: "Date Added", keys: ["date", "createdDate", "created_date", "dateAdded", "date_added", "registeredDate", "addedDate"] },
  { label: "Phone", keys: ["phonenumber", "phoneNumber", "phone_number", "phone", "mobileNumber", "mobile_number", "contactNumber"] },
  { label: "Email", keys: ["email", "emailAddress", "email_address", "emailId", "email_id", "primaryEmail"] },
  { label: "Experience", keys: ["experience", "totalExperience", "total_experience", "experienceInYears", "workExperience"] },
  { label: "Status", keys: ["status", "candidateStatus", "candidate_status", "state"] },
];

const APPLIED_CANDIDATE_COLUMNS = [
  { label: "Name", keys: ["name", "fullName", "full_name", "firstName", "candidateName", "candidate_name"] },
  { label: "Phone", keys: ["phonenumber", "phoneNumber", "phone_number", "phone", "mobileNumber", "contactNumber"] },
  { label: "Job Profile", keys: ["jobProfile", "job_profile", "designation", "role", "title"] },
  { label: "Stage", keys: ["stage", "currentStage", "current_stage", "hiringStage"] },
  { label: "Status", keys: ["status", "candidateStatus", "candidate_status", "state"] },
  { label: "Applied Date", keys: ["appliedDate", "applied_date", "date", "createdDate", "created_date"] },
  { label: "Matching Score", keys: ["matchingScore", "matching_score", "score", "matchScore"] },
  { label: "Source", keys: ["source", "candidateSource", "candidate_source", "sourceType"] },
  { label: "Email", keys: ["email", "emailAddress", "email_address", "emailId", "primaryEmail"] },
  { label: "Experience", keys: ["experience", "totalExperience", "total_experience", "experienceInYears"] },
];

const JOB_COLUMNS = [
  { label: "Position", keys: ["title", "jobTitle", "job_title", "jobProfile", "job_profile", "name", "position"] },
  { label: "Job Code", keys: ["jobCode", "job_code", "code", "referenceCode", "reference_code", "jobId", "id"] },
  { label: "Location", keys: ["location", "city", "place", "jobLocation", "job_location"] },
  { label: "Closing Date", keys: ["closingDate", "closing_date", "endDate", "end_date", "deadline", "expiryDate"] },
  { label: "Applied", keys: ["applied", "appliedCount", "applied_count", "totalApplied", "applicantCount"] },
  { label: "Screen", keys: ["screen", "screenCount", "screen_count", "screened", "screenedCount"] },
  { label: "Shortlist", keys: ["shortlist", "shortlistCount", "shortlist_count", "shortlisted", "shortlistedCount"] },
  { label: "Interview", keys: ["interview", "interviewCount", "interview_count", "interviewScheduled"] },
  { label: "Hired", keys: ["hired", "hiredCount", "hired_count", "totalHired"] },
  { label: "Offer", keys: ["offer", "offerCount", "offer_count", "offered", "offeredCount"] },
  { label: "Status", keys: ["status", "jobStatus", "job_status", "state"] },
];

const INTERVIEW_COLUMNS = [
  { label: "Candidate", keys: ["candidateName", "candidate_name", "name", "fullName"] },
  { label: "Job", keys: ["jobProfile", "job_profile", "jobTitle", "title"] },
  { label: "Round", keys: ["round", "interviewRound", "roundNumber"] },
  { label: "Type", keys: ["interviewType", "interview_type", "type"] },
  { label: "Date", keys: ["interviewDate", "interview_date", "date", "scheduledDate"] },
  { label: "Status", keys: ["status", "interviewStatus", "interview_status"] },
  { label: "Interviewer", keys: ["interviewerName", "interviewer_name", "interviewer", "interviewerFullName"] },
  { label: "Score", keys: ["score", "interviewScore", "interview_score", "rating"] },
  { label: "Duration", keys: ["durationInMinutes", "duration", "duration_in_minutes"] },
];

// ── Resolve first matching key from a row object ────────────────────────────

function resolveValue(row, keys) {
  for (const k of keys) {
    if (row[k] !== undefined) return row[k];
    // Case-insensitive fallback
    const found = Object.keys(row).find(
      (rk) => rk.toLowerCase() === k.toLowerCase()
    );
    if (found !== undefined && row[found] !== undefined) return row[found];
  }
  return null;
}

// ── Detect which columns actually have data ─────────────────────────────────

function filterColumnsWithData(rows, columns) {
  return columns.filter((col) => {
    // Check if at least one row has a non-null value for this column
    return rows.some((row) => {
      const val = resolveValue(row, col.keys);
      return val !== null && val !== undefined && val !== "" && val !== 0;
    });
  });
}

// ── Object card (single record) ─────────────────────────────────────────────

function ObjectCard({ data, focusedColumns }) {
  let entries = [];

  if (focusedColumns) {
    for (const col of focusedColumns) {
      const val = resolveValue(data, col.keys);
      if (val !== null && val !== undefined) {
        entries.push([col.label, val]);
      }
    }
    // If no mapped keys matched, fallback to showing all entries (minus password/token)
    if (entries.length === 0) {
      entries = Object.entries(data).filter(
        ([k]) => !["password", "token"].includes(k.toLowerCase())
      );
    }
  } else {
    entries = Object.entries(data).filter(
      ([k]) => !["password", "token"].includes(k.toLowerCase())
    );
  }

  // Special rendering for matching score results
  const hasMatchingScore = data.matchingScore !== undefined;

  if (hasMatchingScore) {
    const score = data.matchingScore;
    const name = data.candidateName || "Candidate";
    const breakdown = data.scoreBreakdown;

    return (
      <div style={{ padding: "4px 0" }}>
        {/* Score hero */}
        <div style={{
          background: "linear-gradient(135deg, rgba(79,70,229,0.15), rgba(124,58,237,0.15))",
          border: "1px solid rgba(124,58,237,0.35)",
          borderRadius: "10px",
          padding: "20px 16px",
          textAlign: "center",
          marginBottom: breakdown ? "14px" : "0",
        }}>
          <div style={{ fontSize: "13px", color: "#a5b4fc", textTransform: "uppercase", letterSpacing: "1.2px", marginBottom: "6px", fontWeight: 600 }}>
            Match Score — {name}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: "4px" }}>
            <span style={{
              fontSize: "42px", fontWeight: 800,
              background: "linear-gradient(135deg, #818cf8, #c084fc)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              {score !== null && score !== undefined ? Number(score).toFixed(1) : "—"}
            </span>
            <span style={{ fontSize: "18px", color: "#6b7280", fontWeight: 600 }}>/ 100</span>
          </div>
        </div>

        {/* Score breakdown table */}
        {breakdown && typeof breakdown === "object" && (
          <div style={{ borderRadius: "8px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                  <th style={{ padding: "8px 10px", textAlign: "left", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Category</th>
                  <th style={{ padding: "8px 10px", textAlign: "center", color: "#9ca3af", fontWeight: 600 }}>Score</th>
                  <th style={{ padding: "8px 10px", textAlign: "center", color: "#9ca3af", fontWeight: 600 }}>Weight</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(breakdown).map(([cat, info]) => (
                  <tr key={cat} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                    <td style={{ padding: "7px 10px", color: "#d1d5db" }}>{cat.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase())}</td>
                    <td style={{ padding: "7px 10px", textAlign: "center", color: "#e0e7ff", fontWeight: 600 }}>{info.score ?? "—"}</td>
                    <td style={{ padding: "7px 10px", textAlign: "center", color: "#9ca3af" }}>{info.weight ?? "—"}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="hn-kv-grid">
      {entries.map(([key, val]) => (
        <React.Fragment key={key}>
          <span className="hn-kv-key">{key}</span>
          <span className="hn-kv-val">{renderValue(val)}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Focused table — curated columns for known APIs ──────────────────────────

function FocusedTable({ rows, columns }) {
  const [expanded, setExpanded] = useState(false);
  const displayRows = expanded ? rows : rows.slice(0, 5);

  // Only show columns that have at least one non-empty value
  const activeColumns = filterColumnsWithData(rows, columns);

  return (
    <div className="hn-table-wrapper">
      <div className="hn-table-scroll">
        <table className="hn-table">
          <thead>
            <tr>
              {activeColumns.map((col) => (
                <th key={col.label}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, i) => (
              <tr key={i}>
                {activeColumns.map((col) => (
                  <td key={col.label}>{renderValue(resolveValue(row, col.keys))}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 5 && (
        <button
          className="hn-expand-btn"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? "Show less ↑" : `Show all ${rows.length} rows ↓`}
        </button>
      )}
    </div>
  );
}

// ── Generic array table — fallback for unknown APIs ─────────────────────────

function ArrayTable({ rows }) {
  const [expanded, setExpanded] = useState(false);
  const columns = [...new Set(rows.flatMap((r) => Object.keys(r || {})))];
  const displayRows = expanded ? rows : rows.slice(0, 5);

  return (
    <div className="hn-table-wrapper">
      <div className="hn-table-scroll">
        <table className="hn-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, i) => (
              <tr key={i}>
                {columns.map((col) => (
                  <td key={col}>{renderValue(row?.[col])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 5 && (
        <button
          className="hn-expand-btn"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? "Show less ↑" : `Show all ${rows.length} rows ↓`}
        </button>
      )}
    </div>
  );
}

// ── Pick the right column set based on API name ─────────────────────────────

function getColumnsForApi(apiName) {
  const api = (apiName || "").toLowerCase();

  // Applied candidates — specific columns for recruitment pipeline
  // search_candidates internally fetches via get_applied_candidates,
  // so rows have applied-candidate fields (appliedDate, stage, etc.)
  if (api === "get_applied_candidates" || api === "applied_candidates" || api === "search_candidates") {
    return APPLIED_CANDIDATE_COLUMNS;
  }

  // General candidate APIs
  if (
    api === "candidate_list" ||
    api === "candidate_detail" ||
    api.includes("candidate")
  ) {
    return CANDIDATE_COLUMNS;
  }

  // Job APIs
  if (
    api === "job_list" ||
    api === "job_list_closed" ||
    api === "job_detail" ||
    api.includes("job")
  ) {
    return JOB_COLUMNS;
  }

  // Interview APIs
  if (api.includes("interview")) {
    return INTERVIEW_COLUMNS;
  }

  return null;
}

// ── Main DataCard component ─────────────────────────────────────────────────

export default function DataCard({ data, apiName, onHasTable }) {
  // Determine if we have rows (array data)
  const rows = Array.isArray(data)
    ? data
    : Array.isArray(data?.content)
      ? data.content
      : null;

  const isTable = rows !== null && rows.length > 0;

  // Notify parent when this card has table data (for widget expansion)
  useEffect(() => {
    if (onHasTable) {
      onHasTable(isTable);
    }
  }, [isTable, onHasTable]);

  const meta = data?.totalElements !== undefined ? (
    <div className="hn-data-meta">
      <span>{data.totalElements} records found</span>
    </div>
  ) : null;

  // Choose the right column set based on the API name
  const focusedColumns = getColumnsForApi(apiName);

  return (
    <div className="hn-data-card">
      <div className="hn-data-card-header">
        <span className="hn-data-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect width="18" height="18" x="3" y="3" rx="2" />
            <path d="M3 9h18" />
            <path d="M9 21V9" />
          </svg>
          {apiName || "Result Details"}
        </span>
        {meta}
      </div>

      <div className="hn-data-card-body">
        {rows !== null ? (
          rows.length === 0 ? (
            <p className="hn-empty">No results to display.</p>
          ) : focusedColumns ? (
            <FocusedTable rows={rows} columns={focusedColumns} />
          ) : (
            <ArrayTable rows={rows} />
          )
        ) : (
          <ObjectCard data={data} focusedColumns={focusedColumns} />
        )}
      </div>
    </div>
  );
}
